import React, { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Routes } from 'common/enums';
import { getCategoryById } from 'common/assets';
import { CommonLayout } from '../index';
import { Category, Item, AppActions, AppTranslationHelper } from 'common/types';
import { PlusTwoTone, TrashCanOutlineTwoTone, ChevronUpTwoTone, ChevronDownTwoTone } from 'common/icons';
import { getTextIdentifier, setDocumentTitle, exportToJsonFile } from 'common/utils';
import { Search } from 'terminal/components/itemList/components';
import styles from './itemList.module.css';

type ItemListProps = {
  actions: AppActions;
  categories: Category[];
  translation: AppTranslationHelper;
  items: Item[];
};
let selectedCategoryId = 'all';

const ItemList: React.FC<ItemListProps> = ({ categories, items, actions, translation }) => {
  const { formatFinancial, t } = translation;
  const [filteredItems, setFilteredItems] = useState([] as Item[]);
  // Estado para controlar qué grupos están expandidos
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
  // Estado para el filtro de categoría seleccionada
  const [selectedCategory, setSelectedCategory] = useState<string>(selectedCategoryId);

  useEffect(() => {

  }, [])
  useEffect(() => {
    const title = [t('admin.title'), t('admin.items.title')];
    setDocumentTitle(title);
  }, [t]);

  useEffect(() => {
    // Re-calcular itemList cada vez que items cambia
    const newItemList = items
      .filter((item) => !item.isDeleted && item.type !== 'variant' && item.parentItemId === null)
      .sort((a, b) => b.sortOrder - a.sortOrder);
    
    const filteredItems = filterItems(newItemList, '', selectedCategory);
    setFilteredItems(filteredItems);
  }, [items, selectedCategory]);

  // Función para expandir o colapsar un grupo
  const toggleGroupExpansion = (groupId: string, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    setExpandedGroups(prevExpanded => {
      if (prevExpanded.includes(groupId)) {
        return prevExpanded.filter(id => id !== groupId);
      } else {
        return [...prevExpanded, groupId];
      }
    });
  };

  const handleOnClickOnDeleteBtn = (event: React.MouseEvent, itemId: string) => {
    event.preventDefault();
    event.stopPropagation();
    actions.item.remove(itemId);
    //if (filteredItems.length) setFilteredItems(filteredItems.filter((item) => item.id != itemId));
  };

  const handleMoveUp = async (event: React.MouseEvent, currentItem: Item) => {
    // Es crucial detener tanto preventDefault como stopPropagation inmediatamente
    event.preventDefault();
    event.stopPropagation();

    const type = currentItem.type
    // Obtenemos la lista actualizada y filtramos por categoría si es necesario
    let currentItemList;
    
    if (type === 'variant') {
      // Para variantes, filtrar los items que sean variantes del mismo padre
      currentItemList = items
        .filter((item: Item) => item.type === 'variant' && item.parentItemId === currentItem.parentItemId && !item.isDeleted)
        .sort((a: Item, b: Item) => b.sortOrder - a.sortOrder);
    } else {
      // Para items regulares, usar la lista filtrada por categoría actual
      if (selectedCategory !== 'all') {
        currentItemList = items
          .filter((item: Item) => 
            !item.isDeleted && 
            item.type !== 'variant' && 
            item.parentItemId === null &&
            ((selectedCategory === 'root' && item.parentId === 'root') || item.parentId === selectedCategory))
          .sort((a: Item, b: Item) => b.sortOrder - a.sortOrder);
      } else {
        currentItemList = getItemList();
      }
    }
    
    const currentIndex = currentItemList.findIndex(item => item.id === currentItem.id);

    // Si ya está en la primera posición o no se encuentra, no hacer nada
    if (currentIndex <= 0) return;

    // Obtener el ítem que está antes del actual (con menor sortOrder)
    const previousItem = currentItemList[currentIndex - 1];

    // Intercambiar sortOrder usando la acción swapSortOrder
    actions.item.swapSortOrder(currentItem, previousItem);


  };

  const handleMoveDown = async (event: React.MouseEvent, currentItem: Item) => {
    // Es crucial detener tanto preventDefault como stopPropagation inmediatamente
    event.preventDefault();
    event.stopPropagation();

    const type = currentItem.type
    // Obtenemos la lista actualizada y filtramos por categoría si es necesario
    let currentItemList;
    
    if (type === 'variant') {
      // Para variantes, filtrar los items que sean variantes del mismo padre
      currentItemList = items
        .filter((item: Item) => item.type === 'variant' && item.parentItemId === currentItem.parentItemId && !item.isDeleted)
        .sort((a: Item, b: Item) => b.sortOrder - a.sortOrder);
    } else {
      // Para items regulares, usar la lista filtrada por categoría actual
      if (selectedCategory !== 'all') {
        currentItemList = items
          .filter((item: Item) => 
            !item.isDeleted && 
            item.type !== 'variant' && 
            item.parentItemId === null &&
            ((selectedCategory === 'root' && item.parentId === 'root') || item.parentId === selectedCategory))
          .sort((a: Item, b: Item) => b.sortOrder - a.sortOrder);
      } else {
        currentItemList = getItemList();
      }
    }


    const currentIndex = currentItemList.findIndex(item => item.id === currentItem.id);


    // Si ya está en la última posición o no se encuentra, no hacer nada
    if (currentIndex === -1 || currentIndex >= currentItemList.length - 1) return;

    // Obtener el ítem que está después del actual (con mayor sortOrder)
    const nextItem = currentItemList[currentIndex + 1];

    // Intercambiar sortOrder usando la acción swapSortOrder
    actions.item.swapSortOrder(currentItem, nextItem);


  };

  const renderPresenter = (data: Item) => {
    const { picture, color, name } = data;
    const style = picture ? { backgroundImage: `url(${picture})` } : { backgroundColor: color || 'inherit' };
    return (
      <div className={styles.presenter} style={style}>
        {!data.picture && getTextIdentifier(name)}
      </div>
    );
  };
  const variantItems = items.filter(item => item.type === 'variant').sort((a, b) => b.sortOrder - a.sortOrder);

  // Renderizar un item individual
  const renderItem = (data: Item) => {
    const categoryName = getCategoryById(categories, data.parentId).name;
    const isGroup = data.type === 'group';
    const isExpanded = expandedGroups.includes(data.id);

    return (
      <Fragment key={data.id}>
        <Link
          className={`${styles.entity} ${isGroup ? styles.groupItem : ''}`}
          to={Routes.AdminItemEdit.replace(':id', `${data.id}`)}
          onClick={() => {
            selectedCategoryId = selectedCategory
          }}
        >
          {renderPresenter(data)}
          <div className={`${styles.td} ${styles.tdName}`}>
            {data.name}
            {data.offer && (
              <span className={styles.offerBadge}>{t('admin.offers.offerBadge', 'OFERTA')}</span>
            )}
            {isGroup && (
              <span
                className={`${styles.groupIndicator} ${isExpanded ? styles.expanded : ''}`}
                onClick={(evt) => toggleGroupExpansion(data.id, evt)}
                title={isExpanded ? t('admin.items.collapseGroup') : t('admin.items.expandGroup')}
              >
                {isExpanded ? '▼' : '►'}
              </span>
            )}
          </div>
          <div className={`${styles.td} ${styles.tdCategory}`}>
            {categoryName.replace('Home Screen', t('common.homeScreen'))}
          </div>
          <div className={`${styles.td}`}>{isGroup ? '-' : formatFinancial(data.costPrice)}</div>
          <div className={`${styles.td}`}>{isGroup ? '-' : formatFinancial(data.price)}</div>
          <div className={`${styles.td} ${styles.publicStatus}`} title={data.isPublic ? t('admin.items.isPublic') : t('admin.items.isNotPublic')}>
            {selectedCategory !== 'all' && (
              <div className={styles.orderIconsContainer}>
                <div onClick={(evt: React.MouseEvent) => handleMoveUp(evt, data)} title={t('admin.items.moveUp')}>
                  <ChevronUpTwoTone className={styles.orderIcon} />
                </div>
                <div onClick={(evt: React.MouseEvent) => handleMoveDown(evt, data)} title={t('admin.items.moveDown')}>
                  <ChevronDownTwoTone className={styles.orderIcon} />
                </div>
              </div>
            )}
            <svg
              className={`${styles.visibilityIcon} ${data.isPublic ? styles.visibilityIconActive : styles.visibilityIconInactive}`}
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              {data.isPublic ? (
                // Ojo abierto (visible/público)
                <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
              ) : (
                // Ojo cerrado (invisible/privado)
                <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" />
              )}
            </svg>
          </div>
          <button className="btn btn-link" onClick={(evt) => handleOnClickOnDeleteBtn(evt, data.id)}>
            <TrashCanOutlineTwoTone />
          </button>
        </Link>

        {/* Renderizar variantes si el grupo está expandido */}
        {isGroup && isExpanded && variantItems
          .filter(item => !item.isDeleted && item.type === 'variant' && item.parentItemId === data.id)
          .map(variant => (
            <Link
              key={variant.id}
              className={`${styles.entity} ${styles.variantItem}`}
              to={Routes.AdminItemEdit.replace(':id', `${variant.id}`)}
            >
              {renderPresenter(variant)}
              <div className={`${styles.td} ${styles.tdName}`}>
                <span className={styles.variantIndent}>└─</span>
                {variant.name}
              </div>
              <div className={`${styles.td} ${styles.tdCategory}`}>
                {categoryName.replace('Home Screen', t('common.homeScreen'))}
              </div>
              <div className={`${styles.td}`}>{formatFinancial(variant.costPrice)}</div>
              <div className={`${styles.td}`}>{formatFinancial(variant.price)}</div>
              <div className={`${styles.td} ${styles.publicStatus}`} title={variant.isPublic ? t('admin.items.isPublic') : t('admin.items.isNotPublic')}>
                {(selectedCategory !== 'all' || isExpanded) && (
                  <div className={styles.orderIconsContainer}>
                    <div onClick={(evt) => handleMoveUp(evt, variant)} title={t('admin.items.moveUp')}>
                      <ChevronUpTwoTone className={styles.orderIcon} />
                    </div>
                    <div onClick={(evt) => handleMoveDown(evt, variant)} title={t('admin.items.moveDown')}>
                      <ChevronDownTwoTone className={styles.orderIcon} />
                    </div>
                  </div>
                )}
                <svg
                  className={`${styles.visibilityIcon} ${variant.isPublic ? styles.visibilityIconActive : styles.visibilityIconInactive}`}
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  {variant.isPublic ? (
                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
                  ) : (
                    <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" />
                  )}
                </svg>
              </div>
              <button className="btn btn-link" onClick={(evt) => handleOnClickOnDeleteBtn(evt, variant.id)}>
                <TrashCanOutlineTwoTone />
              </button>
            </Link>
          ))
        }
      </Fragment>
    );
  };

  // Filtrar y ordenar items: no mostrar variantes directamente en el listado principal
  // Mover esta definición para que se recalcule cada vez que se usa
  const getItemList = () => {
    return items
      .filter((item: Item) => !item.isDeleted && item.type !== 'variant' && item.parentItemId === null)
      .sort((a: Item, b: Item) => b.sortOrder - a.sortOrder); // Ordenar de forma descendente por sortOrder
  };

  // Filtrar por categoría y término de búsqueda
  const filterItems = (items: Item[], searchTerm: string = '', categoryId: string = 'all') => {
    const filtered = items.filter((item: Item) => {
      // Filtro por término de búsqueda
      const matchesSearchTerm = !searchTerm || searchTerm.length <= 1 ||
        item.name.toUpperCase().includes(searchTerm.toUpperCase());

      // Filtro por categoría
      const matchesCategory =
        categoryId === 'all' ||
        (categoryId === 'root' && item.parentId === 'root') ||
        item.parentId === categoryId;

      return matchesSearchTerm && matchesCategory;
    })
    // Asegurar que los elementos están ordenados por sortOrder
    return filtered.sort((a, b) => b.sortOrder - a.sortOrder);
  };

  // Manejar cambio en el filtro de categoría
  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const categoryId = e.target.value;
    setSelectedCategory(categoryId);
    setFilteredItems(filterItems(getItemList(), '', categoryId));
  };

  // Manejar cambio en el término de búsqueda
  const handleSearchChange = (searchTerm: string) => {
    const currentItemList = getItemList();
    if (searchTerm.length > 1 && currentItemList.length) {
      setFilteredItems(filterItems(currentItemList, searchTerm, selectedCategory));
    } else if (filteredItems.length || !searchTerm.length) {
      // Si se borra la búsqueda, mostrar según la categoría seleccionada
      if (selectedCategory === 'all') {
        setFilteredItems([]);
      } else {
        setFilteredItems(filterItems(currentItemList, '', selectedCategory));
      }
    }
  };

  const renderHead = (
    <Fragment>
      <div className={styles.title}>{t('admin.items.title')}</div>
      <div className="d-flex align-items-center">
        <Search
          onChange={handleSearchChange}
        />
        <select
          className="form-control ml-2"
          style={{ width: 'auto', minWidth: '150px' }}
          value={selectedCategory}
          onChange={handleCategoryChange}
        >
          <option value="all">{t('admin.items.allCategories', 'Todas las categorías')}</option>
          <option value="root">{t('admin.items.mainCategory', 'Principal')}</option>
          {categories
            .filter((category: Category) => !category.isDeleted)
            .map(category => (
              <option key={category.id} value={category.id}>
                {category.name.replace('Home Screen', t('common.homeScreen'))}
              </option>
            ))}
        </select>
      </div>
      <Link className="btn btn-primary ml-2" to={Routes.AdminItemCreate}>
        <PlusTwoTone />
        <span>{t('admin.items.addItemLabel')}</span>
      </Link>
      <button className="btn btn-outline-primary ml-2 mr-4" onClick={() => exportToJsonFile(items)}>
        {t('common.exportToJSON')}
      </button>
    </Fragment>
  );

  const renderBody = (
    <Fragment>
      {getItemList().length ? (
        (filteredItems.length || selectedCategory !== 'all' ? filteredItems : getItemList()).map((item) => renderItem(item))
      ) : (
        <div className={styles.empty}>{t('admin.items.emptyListMessage')}</div>
      )}
      {getItemList().length > 0 && filteredItems.length === 0 && (selectedCategory !== 'all' || filteredItems.length === 0) && (
        <div className={styles.empty}>{t('admin.items.noFilteredResults', 'No se encontraron resultados con los filtros aplicados')}</div>
      )}
    </Fragment>
  );

  return <CommonLayout head={renderHead} body={renderBody} />;
};

export default ItemList;
